import * as React from 'react';
import { action } from 'mobx';
import {
  PageProps,
  PageToolEvents,
  RowUpdate,
} from '../../../../constants';
import { AccountTemplate } from './Account.template';
import { AccountModel } from './Account.model';
import { TableLayout } from '../../../../components';
import { AcccdItem, InfinityRetrieve, SpjangItem } from '../../../../models/common';
import { BankItem } from '../../../human/staff/PersonnelResistration/PersonnelResistration';
import { ConfirmWarning } from '../../../../utils/confirm';
import { PageComponent } from '../../../../utils';
import Confirm from '../../../../utils/confirm/Confirm';
import { AccountModalModel } from './AccountModal.model';

interface AccountState {
  searchQuery: string;
  accountList: Array<AccountModel>;
  data: AccountModel;
  focusedAccount?: AccountModel;
  banks?: Array<BankItem>;
  acccds?: Array<AcccdItem>;
  spjangcds?: Array<SpjangItem>;
  accamt: string; // 월 추가비용
  acccnt: string; // 연동계좌수

  // 연동하기 팝업
  accountModal: boolean;
  accountModalTitle: string;
  accountModalData: AccountModalModel;
}

/**
 * 컨트롤러
 * @window w_s004
 * @category 계좌번호등록
 */
export class Account extends PageComponent<PageProps, AccountState>
  implements PageToolEvents {
  updatedRows?: Array<AccountModel>;

  table: React.RefObject<TableLayout> = React.createRef();

  infinity?: InfinityRetrieve;

  constructor(props: PageProps, context: any) {
    super(props, context);
    this.props.onMount && this.props.onMount(this);

    this.state = props.state || {
      searchQuery: '',
      accamt: '',
      acccnt: '',
      accountList: [],
      banks: [],
      acccds: [],
      spjangcds: [],

      // 연동하기 팝업
      accountModal: false,
      accountModalTitle: '',
      AccountModalModel: new AccountModalModel(),
    };
  }

  @action
  async onFirstOpenEvent() {
    const { actionStore: api } = this.props;

    // 금융기관
    let data = await api.dropdown('wf_dd_s004');
    if (!data) return;
    this.setState({ banks: data.items });

    // 계정
    data = await api.dropdown('wf_dd_s004_acccd');
    if (!data) return;
    this.setState({ acccds: data.items });

    // 사업장
    data = await api.dropdown('wf_dd_spjangcd');
    if (!data) return;
    this.setState({ spjangcds: data.items });

    await this.onRetrieveEvent();
  }

  @action
  async onRetrieveEvent(autoLoad: boolean = true) {
    const { actionStore: api } = this.props;
    this.updatedRows = [];

    // 무한 스크롤바 헬퍼 초기화
    this.infinity = new InfinityRetrieve(
      {
        as_nm: this.state.searchQuery,
      },
      (params) => api.retrieve(params),
      (items) => {
        this.setState({
          accountList: [...this.state.accountList, ...items],
        }, () => this.table.current?.update());
      },
      async () => {
        await this.SS({
          accountList: [],
        });
        await this.infinity?.retrieve();
      },
    );

    // 상단 조회 버튼을 누를때는 기존 배열 초기화
    await this.SS({
      accountList: [],
    });
    if (!autoLoad) return;
    const data = await this.infinity?.retrieve();
    await this.SS({
      accamt: data?.accamt || '',
      acccnt: data?.acccnt || '',
    });

    await this.table.current?.update(true);
    this.state.accountList.length > 0 && this.table.current?.setFocus(0, 3);
  }

  @action
  async onNewEvent() {
    if (this.state.accountList.some((x) => x.isNew)) {
      ConfirmWarning.show('경고', '한번에 한 행만 추가하실 수 있습니다. 저장 후 다음 행을 등록해주세요.');
      return;
    }
    if (this.state.searchQuery !== '') {
      await this.SS({
        searchQuery: '',
      });
    }
    const { actionStore: api } = this.props;
    const data = await api.new();
    if (!data) return;

    // 신규시 itemchanged를 타지않아 bankcd5/bankcd가 비어있음 → 직접 호출해 채움
    const result = await api.fxExec(
      'dw_list_itemchanged',
      {
        itemname: 'bank',
        data: data.bank,
        new: '1',
      },
    );

    this.setState({
      accountList: [...this.state.accountList, new AccountModel({
        ...data,
        banknm: result?.banknm || data.banknm,
        bankcd5: result?.bankcd5 || data.bankcd5,
        bankcd: result?.bankcd || data.bankcd,
      }, true)],
    }, async () => {
      await this.table.current?.update(true);
      this.table.current?.setFocus(this.state.accountList.length - 1, 3);
    });
  }

  @action
  async onSaveEvent() {
    const { actionStore: api } = this.props;
    if (await api.save({
      items: this.updatedRows,
    }, this.state.focusedAccount?.isNew || false)) {
      this.updatedRows = [];
      this.table.current?.resetUpdates();
    }
    await this.onRetrieveEvent();
  }

  @action
  async onDeleteEvent() {
    const { actionStore: api } = this.props;
    const text = `${this.state.focusedAccount?.banknm} ${this.state.focusedAccount?.accnum}`;
    await api.delete(text, this.state.focusedAccount) && await this.onRetrieveEvent();
  }

  @action
  onUpdatedRows(rows: any, updatedRows: any) {
    this.updatedRows = updatedRows;
    this.setState({ data: rows });
  }

  @action
  async onPrintEvent() {
    const { actionStore: api } = this.props;

    if (!ConfirmWarning.assert(this.state.accountList.length > 0,
      '오류', '출력할 내역이 없습니다.')) {
      return;
    }
    await api.printWithElmanManager({
      as_nm: this.state.searchQuery,
    });
  }

  @action
  async onExcelEvent() {
    const { actionStore: api } = this.props;

    if (!ConfirmWarning.assert(this.state.accountList.length > 0,
      '오류', '엑셀변환할 내역이 없습니다.')) {
      return;
    }
    await api.excel({
      as_nm: this.state.searchQuery,
    });
  }

  @action
  async onRowFocusEvent(item: AccountModel) {
    await this.SS({ focusedAccount: item });
  }

  @action
  async backItemChanged(rowUpdate: RowUpdate, x: AccountModel, name: string, value: string) {
    const { actionStore: api } = this.props;
    const result = await api.fxExec(
      'dw_list_itemchanged',
      {
        itemname: name,
        data: value,
        new: x.isNew ? '1' : '0',
      },
    );

    rowUpdate(new AccountModel({
      ...x,
      bank: value,
      banknm: result.banknm,
      bankcd5: result.bankcd5,
    }, x.isNew));
  }

  /* 연동하기 팝업창 */
  @action
  async addAccountModal(x: AccountModel) {
    await this.onRowFocusEvent(x);
    if (this.state.focusedAccount?.ACCNAME.trim() === '') {
      ConfirmWarning.show('확인', '계좌연동에는 계좌명칭이 있어야 합니다!\n 계좌명칭 입력 하신 뒤 저장 후 연동하기 버튼을 눌러주세요!');
      return;
    }
    await this.SS({
      accountModal: true,
    });

    await this.addAccountModalRetrieve();
  }

  @action
  async addAccountModalRetrieve() {
    const { actionStore: api } = this.props;

    const data = await api.fxExec('retrieve', {
      sub: 'w_popup_s004_bankda',
      bank: this.state.focusedAccount?.bank,
      bankcd: this.state.focusedAccount?.bankcd,
      accnum: this.state.focusedAccount?.accnum,
    });

    await this.SS({
      accountModalTitle: data.alert,
      accountModalData: new AccountModalModel(data),
    });
  }

  @action
  async accountModalSave() {
    const { actionStore: api } = this.props;

    const result = await Confirm.ask('선택', '가입이 이루어진 후에는 해당월에 탈퇴처리가 되지 않습니다.\n이점 유의하신 후 가입요청을 해주세요\n가입을 신청하시겠습니까?', '아니오', '예');

    if (!result) {
      ConfirmWarning.show('닫기', '취소되었습니다');
      return;
    }

    await api.fxExec('save', {
      sub: 'w_popup_s004_bankda',
      new: this.state.focusedAccount?.new,
      custcd: this.state.accountModalData?.custcd,
      bank: this.state.accountModalData?.bank,
      bankcd: this.state.accountModalData?.bankcd,
      accnum: this.state.accountModalData?.accnum,
      signkey: this.state.accountModalData?.signkey,
      signpass: this.state.accountModalData?.signpass,
      signkey2: this.state.accountModalData?.signkey2,
      signpass2: this.state.accountModalData?.signpass2,
      signchk: this.state.accountModalData?.signchk,
      signflag: this.state.accountModalData?.signflag,
      bankflag: this.state.accountModalData?.bankflag,
      saupnum: this.state.accountModalData?.saupnum,
      bankda_cd: this.state.accountModalData?.bankda_cd,
      accname: this.state.accountModalData?.accname,
    }) && await this.accountModalClose();
  }

  @action
  async accountModalClose() {
    await this.SS({
      accountModal: false,
      accountModalData: new AccountModalModel(),
      accountModalTitle: '',
    });
    await this.onRetrieveEvent();
  }

  render() {
    return <AccountTemplate
      scope={this}
      update={(change, callback) => {
        this.setState({
          ...this.state,
          ...change,
        }, () => callback && callback());
      }}
    />;
  }
}
